'use client';

import { useState, useRef, useEffect, createContext, useContext } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import { ShareTriggerIcon } from './share-icons';
import { BTN } from './SocialShareButtons';

/* ── Context ─────────────────────────────────────────────────────── */

const SharePopoverContext = createContext({ inMenu: false });
export const useSharePopover = () => useContext(SharePopoverContext);

/* ── Types ────────────────────────────────────────────────────────── */

interface SharePopoverProps {
  compact?: boolean;
  bare?: boolean;
  children: React.ReactNode;
}

/* ── Component ───────────────────────────────────────────────────── */

export function SharePopover({ compact, bare, children }: SharePopoverProps) {
  const lang = useCalendarStore((s) => s.lang);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /* ── Close on outside click & Escape ───────────────────────── */

  useEffect(() => {
    if (!popoverOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPopoverOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [popoverOpen]);

  const shareLabel = uiFb('ui.share', 'Share', lang);

  const popoverMenu = (
    <div
      role="menu"
      className="absolute right-0 top-full z-50 mt-1 flex items-center gap-0.5 content-card p-1 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
    >
      <SharePopoverContext.Provider value={{ inMenu: true }}>
        {children}
      </SharePopoverContext.Provider>
    </div>
  );

  const trigger = (
    <button
      ref={triggerRef}
      onClick={() => setPopoverOpen(!popoverOpen)}
      className={BTN}
      aria-label={shareLabel}
      aria-haspopup="true"
      aria-expanded={popoverOpen}
    >
      <ShareTriggerIcon />
    </button>
  );

  /* ── Compact: always popover ───────────────────────────────── */

  if (compact) {
    return (
      <div className="relative inline-block" ref={popoverRef}>
        {trigger}
        {popoverOpen && popoverMenu}
      </div>
    );
  }

  /* ── Default: popover on mobile, inline on md+ ─────────────── */

  return (
    <>
      <div className="md:hidden relative inline-block" ref={popoverRef}>
        {trigger}
        {popoverOpen && popoverMenu}
      </div>

      <div className={bare
        ? 'hidden md:flex items-center gap-0.5'
        : 'hidden md:flex items-center gap-0.5 content-card p-1'
      }>
        <SharePopoverContext.Provider value={{ inMenu: false }}>
          {children}
        </SharePopoverContext.Provider>
      </div>
    </>
  );
}
